/**
 * FlatNavigation Component
 *
 * Simple flat navigation list for guide and customer users with:
 * - Flat list of navigation items (no categories)
 * - Badge support for unread counts
 * - Active state highlighting
 * - Icon-only mode for tablet (with tooltips)
 *
 * Epic: MVP 1.21.2 - Dashboard Design System & Notifications (SCRUM-459)
 * Component: Guide/Customer Navigation
 *
 * @component
 * @param {Object} props
 * @param {Array} props.navigation - Flat array of navigation items
 * @param {string} props.activeId - Currently active navigation item ID
 * @param {Function} props.onNavigate - Navigation callback function
 * @param {boolean} props.isCollapsed - Icon-only mode (tablet)
 */

import React from 'react';
import PropTypes from 'prop-types';
import NotificationBadge from '../common/NotificationBadge';

const FlatNavigation = ({
  navigation,
  activeId,
  onNavigate,
  isCollapsed = false
}) => {
  const handleItemClick = (item) => {
    if (onNavigate) {
      onNavigate(item.id, item.path);
    }
  };

  return (
    <nav style={styles.nav}>
      {navigation.map((item) => {
        const Icon = item.icon;
        const isActive = item.id === activeId;

        return (
          <button
            key={item.id}
            onClick={() => handleItemClick(item)}
            style={{
              ...styles.navItem,
              ...(isActive && styles.navItemActive),
              ...(isCollapsed && styles.navItemCollapsed),
            }}
            title={isCollapsed ? item.label : undefined}
          >
            <Icon
              size={20}
              style={{
                ...styles.icon,
                color: isActive
                  ? 'var(--color-primary)'
                  : 'var(--color-gray-500)',
              }}
            />
            {!isCollapsed && (
              <>
                <span style={styles.label}>{item.label}</span>
                {item.badge && item.badge > 0 && (
                  <NotificationBadge count={item.badge} small />
                )}
              </>
            )}
            {isCollapsed && item.badge && item.badge > 0 && (
              <div style={styles.collapsedBadge}>
                {item.badge > 99 ? '99+' : item.badge}
              </div>
            )}
          </button>
        );
      })}
    </nav>
  );
};

const styles = {
  nav: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-1)',
  },

  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-3)',
    padding: 'var(--space-2) var(--space-3)',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    transition: 'all var(--transition-fast)',
    height: 'var(--nav-item-height)',
    width: '100%',
    textAlign: 'left',
    position: 'relative',
  },

  navItemCollapsed: {
    justifyContent: 'center',
    padding: 'var(--space-2)',
  },

  navItemActive: {
    backgroundColor: 'var(--color-primary-light)',
    fontWeight: 'var(--font-weight-medium)',
  },

  icon: {
    flexShrink: 0,
  },

  label: {
    fontSize: 'var(--font-size-base)',
    color: 'var(--color-text-primary)',
    flex: 1,
  },

  collapsedBadge: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    minWidth: '16px',
    height: '16px',
    borderRadius: 'var(--radius-full)',
    backgroundColor: 'var(--color-error)',
    color: 'var(--color-white)',
    fontSize: '10px',
    fontWeight: 'var(--font-weight-bold)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 4px',
  },
};

FlatNavigation.propTypes = {
  navigation: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
      path: PropTypes.string,
      badge: PropTypes.number,
    })
  ).isRequired,
  activeId: PropTypes.string,
  onNavigate: PropTypes.func,
  isCollapsed: PropTypes.bool,
};

export default FlatNavigation;
